import React from 'react';
import { useCustom, useCustomMutation, useNotification } from '@refinedev/core';
import { Alert, Button, Card, Space, Tag, theme } from 'antd';
import { EfxMembershipDto, EfxMembershipStateDto } from '@wellverse/shared-ts';

/** How often the card reads the stored answer again while it is open. */
export const LIVE_REFRESH_MS = 15 * 1000;

const STATE_LABELS: Record<EfxMembershipStateDto, { label: string; color: string; hint?: string }> = {
  MEMBER: { label: 'EFX MEMBER', color: 'green' },
  EXPIRED: { label: 'EXPIRED', color: 'orange', hint: 'EFX knows this number, but the membership has ended.' },
  NOT_MEMBER: { label: 'NOT A MEMBER', color: 'default', hint: 'EFX has no member with this phone number.' },
  NO_VERIFIED_PHONE: {
    label: 'NO VERIFIED PHONE',
    color: 'default',
    hint: 'EFX membership is matched by phone number, and this account has no verified one.',
  },
  UNSUPPORTED_REGION: {
    label: 'UNSUPPORTED NUMBER',
    color: 'default',
    hint: 'EFX only knows Hong Kong, Macau and mainland China numbers.',
  },
  NOT_CONFIGURED: {
    label: 'EFX CHECK OFF',
    color: 'default',
    hint: 'This environment has no EFX API address or key (EFX_API_BASE_URL / EFX_API_KEY).',
  },
  UNAVAILABLE: { label: 'EFX UNREACHABLE', color: 'red', hint: 'EFX could not be reached and there is no earlier answer.' },
};

/**
 * Whether EFX recognises this member, read from EFX by the phone number on
 * the account. Support cannot set it here — EFX is the authority — but can ask
 * EFX again, for the member who says they have just joined or renewed.
 */
export const UserEfxMembership: React.FC<{ userId: string }> = ({ userId }) => {
  const { token } = theme.useToken();
  const { open } = useNotification();

  const { data, isLoading, refetch } = useCustom<EfxMembershipDto>({
    url: `/admin/efx/members/${userId}`,
    method: 'get',
    // The app re-checks EFX when the member opens it or pulls down on Home
    // (10/15, the customer: show that here without reloading the page).
    queryOptions: { refetchInterval: LIVE_REFRESH_MS, refetchOnWindowFocus: true },
  });
  const { mutate, isLoading: isChecking } = useCustomMutation<EfxMembershipDto>();

  const membership = data?.data;
  const view = membership ? STATE_LABELS[membership.state] : undefined;

  const checkNow = () => {
    mutate(
      { url: `/admin/efx/members/${userId}/refresh`, method: 'post', values: {} },
      {
        onSuccess: () => {
          open?.({ type: 'success', message: 'Checked with EFX' });
          refetch();
        },
        onError: (error) => {
          open?.({ type: 'error', message: 'Could not check with EFX', description: error?.message });
        },
      },
    );
  };

  return (
    <Card title="EFX Membership" size="small" loading={isLoading} style={{ marginTop: 24 }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Space size={12} wrap>
          <Tag color={view?.color}>{view?.label ?? '—'}</Tag>
          {membership?.memberNo && <span>Member no. {membership.memberNo}</span>}
          {membership?.expirationDate && (
            <span style={{ color: token.colorTextSecondary }}>
              {membership.state === 'MEMBER' ? 'until' : 'ended'} {membership.expirationDate}
            </span>
          )}
          <Button size="small" loading={isChecking} onClick={checkNow}>
            Check with EFX now
          </Button>
        </Space>
        {view?.hint && <span style={{ color: token.colorTextSecondary }}>{view.hint}</span>}
        {membership?.checkedAt && (
          <span style={{ color: token.colorTextSecondary }}>
            Last answer from EFX: {new Date(membership.checkedAt).toLocaleString()}
          </span>
        )}
        {membership?.lastError && (
          <Alert
            type="warning"
            showIcon
            message="The latest check with EFX failed; showing the earlier answer."
            description={membership.lastError}
          />
        )}
      </Space>
    </Card>
  );
};
